/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from './../../message';
import OperationMetadataHelpers from './../helpers/operationmetadatahelpers';
import { IDescriptorBody } from '../../descriptor';
import OperationMetadataDescriptor, { IOperationMetadata, IOperationMetadataJSON } from '../descriptors/operationmetadatadescriptor';

export interface ICollaborativeEditingConnectResponse extends IMessage {
	channel: string;
	offset: number;
	version: number;
	sessionId: string;
	buffers: Buffer[] | Uint8Array[];
	types: number[];
	metadata: IOperationMetadataJSON[];
	initData: string;
	isInitialized: boolean;
	lastOperationId?: string;
	sessionCreatedAt: string | null;
}

interface ICollaborativeEditingConnectResponseParams {
	channel: string;
	version: number;
	sessionId?: string;
	buffers?: Buffer[] | Uint8Array[];
	types?: number[];
	offset?: number;
	metadata?: IOperationMetadata[];
	initData?: string | null;
	lastOperationId?: string;
	sessionCreatedAt: Date | null;
}

interface ICollaborativeEditingConnectResponseData {
	buffers: Buffer[] | Uint8Array[];
	types: number[];
	baseVersion: number;
	lastOperationId?: string;
	sessionCreatedAt: Date | null;
}

export default class CollaborativeEditingConnectResponse extends Message {
	public static readonly DESCRIPTOR_NAME: string = 'CollaborativeEditingConnectResponse';

	public static readonly DESCRIPTOR: IDescriptorBody = /* #__PURE__ -- @preserve */ ( () => ( {
		fields: {
			channel: {
				type: 'string',
				id: 1
			},
			offset: {
				type: 'uint32',
				id: 2
			},
			version: {
				type: 'uint32',
				id: 3
			},
			buffers: {
				rule: 'repeated',
				type: 'bytes',
				id: 4
			},
			types: {
				rule: 'repeated',
				type: 'uint32',
				id: 5
			},
			metadata: {
				rule: 'repeated',
				type: OperationMetadataDescriptor.DESCRIPTOR_NAME,
				id: 7
			},
			initData: {
				type: 'string',
				id: 8
			},
			isInitialized: {
				type: 'bool',
				id: 9
			},
			sessionId: {
				type: 'string',
				id: 10
			},
			lastOperationId: {
				type: 'string',
				id: 11
			},
			sessionCreatedAt: {
				type: 'string',
				id: 12
			}
		}
	} ) )();

	/**
	 * @deprecated - Use CollaborativeEditingConnectResponse.create factory method instead.
	 *
	 * @param channel - Name of channel on which messages will be published.
	 * @param version - Version of the document.
	 * @param buffers - List of operations compressed to the binary format.
	 * @param types - List of compressor identifiers.
	 * @param offset - Number of changes in previous editing sessions.
	 * @param metadata - Metadata of changes.
	 * @param initData - Initialization data of the document.
	 */
	public constructor(
		public readonly channel: string,
		public readonly version: number,
		public readonly buffers: Buffer[] | Uint8Array[] = [],
		public readonly types: number[] = [],
		public readonly offset: number = 0,
		public readonly metadata: IOperationMetadata[] = [],
		public readonly initData: string | null = null,
		public readonly sessionId: string = '',
		public readonly lastOperationId: string = '',
		// default null for backward compatibility - new client and old server
		public readonly sessionCreatedAt: Date | null = null
	) {
		super();
	}

	public get data(): ICollaborativeEditingConnectResponseData {
		return {
			buffers: this.buffers,
			types: this.types,
			baseVersion: this.version - this.types.length,
			lastOperationId: this.lastOperationId,
			sessionCreatedAt: this.sessionCreatedAt
		};
	}

	public get wereChangesApplied(): boolean {
		return !( this.types?.length ) && this.initData === null;
	}

	public toJSON(): ICollaborativeEditingConnectResponse {
		return {
			channel: this.channel,
			version: this.version,
			sessionId: this.sessionId,
			buffers: this.buffers,
			types: this.types,
			offset: this.offset,
			metadata: OperationMetadataHelpers.removeUnnecessaryMetadata( this.types, this.metadata )
				.map( OperationMetadataDescriptor.toJSON ),
			initData: this.initData ?? '',
			isInitialized: typeof this.initData === 'string',
			lastOperationId: this.lastOperationId,
			sessionCreatedAt: this.sessionCreatedAt ? this.sessionCreatedAt.toISOString() : null
		};
	}

	public static create( params: ICollaborativeEditingConnectResponseParams ): CollaborativeEditingConnectResponse {
		return new CollaborativeEditingConnectResponse(
			params.channel,
			params.version,
			params.buffers,
			params.types,
			params.offset,
			params.metadata,
			params.initData,
			params.sessionId,
			params.lastOperationId,
			params.sessionCreatedAt
		);
	}

	public static fromJSON( data: ICollaborativeEditingConnectResponse ): CollaborativeEditingConnectResponse {
		let initData: string | null = null;

		if ( data.isInitialized === true ) {
			initData = data.initData ?? '';
		}
		// backward compatibility - new client and old server
		else if ( !( typeof data.isInitialized === 'boolean' ) && data.initData ) {
			initData = data.initData;
		}

		return CollaborativeEditingConnectResponse.create( {
			channel: data.channel,
			version: data.version,
			buffers: data.buffers,
			types: data.types,
			offset: data.offset,
			metadata: OperationMetadataHelpers
				.prepareMetadataForOperations( data.types, data.metadata )
				.map( OperationMetadataDescriptor.fromJSON ),
			initData,
			sessionId: data.sessionId,
			lastOperationId: data.lastOperationId,
			sessionCreatedAt: data.sessionCreatedAt ? new Date( data.sessionCreatedAt ) : null
		} );
	}
}

